import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Map from './Map'
import { Grid, makeStyles, Typography } from '@material-ui/core'
import LocationAddEditDialog from './locationAddEditDialog/LocationAddEditDialog'
import LocationList from './locationList/LocationList'
import LocationDetail from './locationDetail/LocationDetail'
import { LocationInfo, Location } from '../../shared/types/location'
import { useCalcHeightToBottom } from '../../utils/ui'
import { useTranslation } from 'react-i18next'
import { SplitButton } from '../../shared/components/splitButton'
import { Coordinates } from '../../shared/types/common'
import { setSelectedLocationId, fetchLocations } from '../../redux/actions/locations'
import { fetchTenants, fetchMarkets } from '../../redux/actions/dashboard'
import { fetchAllChargerTags, fetchAllLocationTags } from '../../redux/actions/tags'

const useStyles = makeStyles((theme) => ({
    headerWrapper: {
        padding: theme.spacing(1),
    },
    actionButtonWrapper: {
        padding: theme.spacing(1, 0),
        textAlign: 'right',
    },
    listWrapper: {
        paddingTop: theme.spacing(2),
    },
    mapWrapper: {
        paddingTop: theme.spacing(2),
        width: '100%',
    },
}))

export default function Locations() {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const selectedLocationId = useSelector((state: any) => state.locations.selectedId.value)

    const locations = useSelector((state: any) => state.locations.items.value) ?? []

    const editedLocation = useSelector((state: any) => state.locations.edit.value)
    const addedLocation = useSelector((state: any) => state.locations.add.value)
    const deletedLocation = useSelector((state: any) => state.locations.delete.value)

    const selectedLocation = useSelector((state: any) =>
        state.locations.items?.value?.find((loc: LocationInfo) => loc.id === selectedLocationId)
    )

    // Jednotný lokálny stav pre search
    const [search, setSearch] = useState('')
    const [mapCenter, setMapCenter] = useState<Coordinates>()

    const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false)
    const [addDialogInitialModel, setAddDialogInitialModel] = useState<Location>()
    const [mapAdding, setMapAdding] = useState<boolean>(false)

    const mapHeightFill = useCalcHeightToBottom('locationsMapWrapper')

    // Prefiltrované lokácie, ktoré zdieľame so zoznamom aj s mapou
    const filteredLocations = useMemo(() => {
        const searchLower = search.toLowerCase().trim()
        if (!searchLower) return locations

        return locations.filter((location: LocationInfo) =>
            location?.name?.toLowerCase().includes(searchLower)
        )
    }, [locations, search])

    useEffect(() => {
        dispatch(fetchLocations(tenantId))
    }, [dispatch, tenantId, editedLocation, addedLocation, deletedLocation])

    const onLocationDetailClose = () => {
        dispatch(setSelectedLocationId(undefined))
    }

    const openAddDialog = () => {
        setAddDialogInitialModel(undefined);
        setAddDialogOpen(true);
    }

    const addButtonOptions = {
        primary: {
            label: t('locations.newLocation'),
            function: () => openAddDialog(),
        },
        options: [
            {
                label: t('locations.newByManualEntry'),
                function: () => openAddDialog(),
            },
            {
                label: t('locations.newByMap'),
                function: () => setMapAdding(true),
            },
        ],
    }

    const onAddDialogClose = (location: Location) => {
        setAddDialogOpen(false)
        setAddDialogInitialModel(undefined)
    }

    const onMapAddEnd = (coordinates?: { latitude: number; longitude: number }) => {
        setMapAdding(false);
        if (coordinates) {
            setAddDialogInitialModel({
                id: '',
                name: '',
                description: '',
                city: '',
                street: '',
                postalCode: '',
                houseNum: '',
                phoneNumber: '',
                active: true,
                hidden: false,
                includeInHubject: false,
                allowAnonymous: false,
                emailNotification: false,
                flexiCharging: false,
                preferFlexiCharging: false,
                tenantSpecific: false,
                marketId: null,
                tenantId: null,
                latitude: coordinates.latitude,
                longitude: coordinates.longitude,
                pricingConditions: [],
                images: [],
                tags: [],
                loadBalancers: [],
                whitelist: [],
                chargers: null,
            });
            setAddDialogOpen(true);
        }
    };

    const onLocationSelectedFromMap = (location: LocationInfo) => {
        dispatch(setSelectedLocationId(location.id))
    }

    const onLocationSelectedFromList = (location: LocationInfo) => {
        dispatch(setSelectedLocationId(location.id))
        setMapCenter({ lat: location.latitude, lng: location.longitude })
    }

    useEffect(() => {
        if (selectedLocation) {
            setMapCenter({ lat: selectedLocation.latitude, lng: selectedLocation.longitude })
        }
    }, [selectedLocation])

    // useEffect(() => {
    //     if (selectedLocationId) {
    //         // Ak sa vybrala lokácia (napr. preklikom z TagCard),
    //         // vyčistíme search, aby bola v zozname určite viditeľná a nezostala odfiltrovaná
    //         setSearch('')
    //     }
    // }, [selectedLocationId])
    
    useEffect(() => {
        dispatch(fetchTenants())
        dispatch(fetchMarkets())
        dispatch(fetchAllLocationTags())
        dispatch(fetchAllChargerTags())
    }, [dispatch])


    return (
        <>
            <Grid container spacing={0}>
                <Grid item sm={2} className={classes.headerWrapper}>
                    <Typography align="left" variant="h3">
                        {t('locations.title')}
                    </Typography>
                </Grid>
                <Grid item sm={10} className={classes.actionButtonWrapper}>
                    <SplitButton {...addButtonOptions} />
                    <LocationAddEditDialog
                        open={addDialogOpen}
                        isAdd={true}
                        onClose={onAddDialogClose}
                        initialModel={addDialogInitialModel}
                    />
                </Grid>
                <Grid item sm={4} className={classes.listWrapper}>
                    {/* Odovzdávame prefiltrované lokácie a search stav do zoznamu */}
                    <LocationList
                        locations={filteredLocations}
                        search={search}
                        onSearchChange={setSearch}
                        selectedLocationId={selectedLocationId}
                        onLocationSelected={onLocationSelectedFromList}
                    />
                </Grid>
                <Grid item className={classes.mapWrapper} sm={8}>
                    <div id="locationsMapWrapper" ref={mapHeightFill}>
                        {/* Odovzdávame prefiltrované lokácie aj mape, aby sa na nej schovali nefunkčné/nevyhovujúce špendlíky */}
                        <Map
                            locations={filteredLocations}
                            center={mapCenter}
                            onCenterChanged={setMapCenter}
                            onLocationSelected={onLocationSelectedFromMap}
                            adding={mapAdding}
                            onMapAddEnd={onMapAddEnd}
                        />
                    </div>
                </Grid>
            </Grid>
            <LocationDetail onClose={onLocationDetailClose} locationId={selectedLocationId} />
        </>
    )
}